/*
 * Elexa product core (group A1). Loaded by sections/main-product.liquid and sections/featured-product.liquid.
 *
 * <elexa-product>         One per product section instance. Single source of truth for the selected variant,
 *                         quantity, selling plan and offer; owns the price / label renderer and the add-to-cart
 *                         flow (Elexa.cart.add). Public API: Elexa.products[sectionId]
 *                           .state / .getState()            { variant, options, quantity, sellingPlan, offer, subscription, unavailable }
 *                           .selectVariant(id, { source })  .setOption(position, value, { source })
 *                           .setQuantity(n, { source })      .setSellingPlan(id)
 *                           .submit(triggerButton)           (guard + add-to-cart, used by the sticky bar)
 * <elexa-variant-picker>  Simple Variant Picker block
 * <elexa-variant-bundle>  Variant Bundle cards block
 * <elexa-qty>             Quantity Selector block
 * <elexa-gallery>         Crossfade gallery (thumbnails, dots, arrows, swipe with pinch guard, hover zoom, video)
 *
 * Events on document (CustomEvent, bubbles):
 *   emits   elexa:variant-change  { sectionId, variant, product, source, quantity }   (contract)
 *           elexa:option-change   { sectionId, position, value, source }
 *           elexa:quantity-change { sectionId, quantity, source }
 *           elexa:product-render  { sectionId, state }     (after every price/label render; the sticky bar uses it)
 *           elexa:product-error   { sectionId, message }
 *           elexa:product-ready   { sectionId, variant, product }
 *   listens elexa:offer-change    { sectionId, items, label, totalCents, compareCents, valid, error }  (contract;
 *                                 items null = no offer, totalCents null = normal price)
 *           elexa:variant-change / elexa:option-change fired by other blocks for this section (synced, not re-fired)
 *           elexa:subscription-change { sectionId, sellingPlanId, type: percentage|fixed_amount|price, value }
 *           sealsubs:selling_plan_changed (SEAL Subscriptions widget inside the section)
 */
(() => {
  if (customElements.get('elexa-product')) return;

  const Elexa = (window.Elexa = window.Elexa || {});
  const registry = (Elexa.products = Elexa.products || {});
  // A2 contract: this product form consumes elexa:offer-change (A2 keeps its fallbacks out of the way).
  Elexa.offerAware = true;

  const EV = {
    variant: 'elexa:variant-change',
    offer: 'elexa:offer-change',
    option: 'elexa:option-change',
    quantity: 'elexa:quantity-change',
    subscription: 'elexa:subscription-change',
    render: 'elexa:product-render',
    error: 'elexa:product-error',
    ready: 'elexa:product-ready',
  };

  const money = (cents) =>
    typeof Elexa.money === 'function' ? Elexa.money(Math.round(cents || 0)) : (Math.round(cents || 0) / 100).toFixed(2);
  const savePct = (compare, price) => (compare > price && compare > 0 ? Math.round(((compare - price) * 100) / compare) : 0);
  const emit = (name, detail, target = document) => target.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isRTL = (el) => getComputedStyle(el || document.documentElement).direction === 'rtl';
  const fill = (str, vars) => String(str || '').replace(/\[(\w+)\]/g, (m, k) => (k in vars ? vars[k] : m));
  const esc = (s) =>
    String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const dawnPublish = (name, data) => {
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS[name]) {
        publish(PUB_SUB_EVENTS[name], data);
      }
    } catch (e) {
      /* Dawn pub/sub is optional */
    }
  };

  /** Subscription price for one unit (cents). */
  const subscriptionPrice = (price, sub) => {
    if (!sub) return price;
    let out = price;
    if (sub.type === 'percentage') out = price * (1 - sub.value / 100);
    else if (sub.type === 'fixed_amount') out = price - sub.value;
    else if (sub.type === 'price') out = sub.value;
    return Math.max(0, Math.round(out));
  };

  /* ====================================================================== *
   * Variant picker block
   * ====================================================================== */
  class ElexaVariantPicker extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.addEventListener('change', (e) => {
        const group = e.target.closest('[data-ex-option-group]');
        if (!group || !this.contains(group)) return;
        const c = this.closest('elexa-product');
        if (!c || typeof c.setOption !== 'function') return;
        c.setOption(parseInt(group.dataset.position, 10), e.target.value || null, { source: 'picker' });
      });
      const c = this.closest('elexa-product');
      if (c && c.state) this.sync(c);
    }

    sync(c) {
      const s = c.state;
      const variants = c.variants;
      const others = (v, i, prefixOnly) =>
        s.options.every((sel, k) => k === i || sel == null || (prefixOnly && k > i) || v.options[k] === sel);
      this.querySelectorAll('[data-ex-option-group]').forEach((group) => {
        const i = parseInt(group.dataset.position, 10) - 1;
        const selected = s.options[i];
        const select = group.querySelector('select');
        group.querySelectorAll('[data-ex-opt-value]').forEach((input) => {
          const val = input.value;
          const exists = variants.some((v) => v.options[i] === val && others(v, i, true));
          const available = variants.some((v) => v.available && v.options[i] === val && others(v, i, false));
          if (input.tagName === 'OPTION') {
            input.hidden = !exists;
            input.textContent = available ? input.dataset.text : fill(c.strings.valueUnavailable, { value: input.dataset.text });
            input.classList.toggle('is-unavailable', !available);
            input.disabled = !available && val !== selected;
          } else {
            const item = input.closest('[data-ex-opt-item]') || input;
            item.hidden = !exists;
            item.classList.toggle('is-unavailable', !available);
            input.checked = val === selected;
            const label = input.nextElementSibling;
            if (label) label.classList.toggle('is-selected', val === selected);
          }
        });
        if (select) select.value = selected == null ? '' : selected;
        const out = group.querySelector('[data-ex-selected-value]');
        if (out) {
          let text = '';
          if (selected != null) {
            const match = Array.from(group.querySelectorAll('[data-ex-opt-value]')).find((el) => el.value === selected);
            text = match ? match.dataset.label || selected : selected;
          }
          out.textContent = text;
          out.hidden = !text;
        }
      });
      this.syncPrices(c);
    }

    syncPrices(c) {
      const s = c.state;
      const variants = c.variants;
      this.querySelectorAll('[data-ex-opt-pricing]').forEach((el) => {
        const group = el.closest('[data-ex-option-group]');
        if (!group) return;
        const i = parseInt(group.dataset.position, 10) - 1;
        const val = el.dataset.exOptPricing;
        const v =
          variants.find((x) => x.options[i] === val && s.options.every((sel, k) => k === i || sel == null || x.options[k] === sel)) ||
          variants.find((x) => x.options[i] === val);
        if (!v) return;
        const offerStyle = el.dataset.offerStyle === 'true';
        let price = v.price;
        let compare = v.compare_at_price || 0;
        let reference = compare > price ? compare : price;
        if (offerStyle && s.subscription) {
          price = subscriptionPrice(v.price, s.subscription);
          compare = reference;
        } else {
          reference = compare > price ? compare : 0;
        }
        const priceEl = el.querySelector('[data-ex-opt-price]');
        if (priceEl) priceEl.innerHTML = money(price);
        const cmpEl = el.querySelector('[data-ex-opt-compare]');
        if (cmpEl) {
          cmpEl.hidden = !(compare > price);
          cmpEl.innerHTML = compare > price ? money(compare) : '';
        }
        el.querySelectorAll('[data-ex-opt-save]').forEach((save) => {
          const ref = save.dataset.reference === 'compare' ? compare : reference;
          const savings = ref - price;
          save.hidden = !(savings > 0);
          if (savings > 0) {
            save.innerHTML = fill(esc(save.dataset.template), { pct: savePct(ref, price), amount: money(savings) });
          }
        });
      });
    }
  }

  /* ====================================================================== *
   * Variant bundle cards block
   * ====================================================================== */
  class ElexaVariantBundle extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.cards = Array.from(this.querySelectorAll('[data-ex-bundle-card]'));
      this.addEventListener('click', (e) => {
        const card = e.target.closest('[data-ex-bundle-card]');
        if (card) this.choose(card);
      });
      this.addEventListener('keydown', (e) => {
        const card = e.target.closest('[data-ex-bundle-card]');
        if (!card) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.choose(card);
          return;
        }
        const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
        if (!(e.key in keys)) return;
        e.preventDefault();
        let dir = keys[e.key];
        if (isRTL(this) && (e.key === 'ArrowRight' || e.key === 'ArrowLeft')) dir = -dir;
        const enabled = this.cards.filter((x) => x.getAttribute('aria-disabled') !== 'true');
        const pos = enabled.indexOf(card);
        const next = enabled[(pos + dir + enabled.length) % enabled.length];
        if (next) {
          next.focus();
          this.choose(next);
        }
      });
      const c = this.closest('elexa-product');
      if (c && c.state) this.sync(c, 'init');
    }

    choose(card) {
      if (card.getAttribute('aria-disabled') === 'true') return;
      const c = this.closest('elexa-product');
      if (c && typeof c.selectVariant === 'function') c.selectVariant(card.dataset.variantId, { source: 'bundle' });
    }

    sync(c, source) {
      const id = c.state.variant ? String(c.state.variant.id) : null;
      if (!id && source === 'init' && !c.state.unavailable) {
        const first = this.cards.find((x) => x.getAttribute('aria-disabled') !== 'true');
        if (first) {
          Promise.resolve().then(() => c.selectVariant(first.dataset.variantId, { source: 'bundle' }));
          return;
        }
      }
      let focusable = null;
      this.cards.forEach((card) => {
        const on = card.dataset.variantId === id;
        card.setAttribute('aria-checked', on ? 'true' : 'false');
        card.classList.toggle('is-selected', on);
        if (on) focusable = card;
      });
      const fallback = focusable || this.cards.find((x) => x.getAttribute('aria-disabled') !== 'true');
      this.cards.forEach((card) => card.setAttribute('tabindex', card === fallback ? '0' : '-1'));
    }
  }

  /* ====================================================================== *
   * Quantity selector block
   * ====================================================================== */
  class ElexaQty extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.input = this.querySelector('input');
      this.minus = this.querySelector('[data-ex-qty-minus]');
      this.plus = this.querySelector('[data-ex-qty-plus]');
      const ctrl = () => this.closest('elexa-product');
      const stepBy = (dir) => {
        const c = ctrl();
        if (!c || this.locked) return;
        const step = parseInt(this.input.step, 10) || 1;
        c.setQuantity((parseInt(this.input.value, 10) || 0) + dir * step, { source: 'quantity' });
      };
      if (this.minus) this.minus.addEventListener('click', () => stepBy(-1));
      if (this.plus) this.plus.addEventListener('click', () => stepBy(1));
      const commit = () => {
        const c = ctrl();
        if (!c) return;
        const min = parseInt(this.input.min, 10) || 1;
        const max = parseInt(this.input.max, 10) || 0;
        let n = parseInt(this.input.value, 10);
        if (!Number.isFinite(n) || n < min) n = min;
        if (max && n > max) n = max;
        c.setQuantity(n, { source: 'quantity' });
        this.input.value = c.state.quantity;
      };
      this.input.addEventListener('change', commit);
      this.input.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        e.stopPropagation();
        commit();
      });
      const c = ctrl();
      if (c && c.state) c.syncQty();
    }

    update({ value, min, max, step, locked }) {
      if (!this.input) return;
      this.locked = locked;
      this.input.value = value;
      this.input.min = min;
      this.input.step = step;
      if (max) this.input.max = max;
      else this.input.removeAttribute('max');
      this.input.disabled = locked;
      if (this.minus) this.minus.disabled = locked || value - step < min;
      if (this.plus) this.plus.disabled = locked || Boolean(max && value + step > max);
      this.classList.toggle('is-locked', locked);
    }
  }

  /* ====================================================================== *
   * Gallery
   * ====================================================================== */
  class ElexaGallery extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.main = this.querySelector('[data-ex-gallery-main]');
      this.slides = Array.from(this.querySelectorAll('[data-ex-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-ex-thumb]'));
      this.dots = Array.from(this.querySelectorAll('[data-ex-dot]'));
      this.strip = this.querySelector('[data-ex-thumbs]');
      this.variantMedia = new Set(
        String(this.dataset.variantMedia || '')
          .split(',')
          .filter(Boolean)
          .map(Number)
      );
      this.hideOthers = this.dataset.hideOthers === 'true';
      this.autoplay = this.dataset.autoplay === 'true';
      this.loop = this.dataset.loop === 'true';
      this.index = Math.max(
        0,
        this.slides.findIndex((s) => s.classList.contains('is-active'))
      );
      this.addEventListener('click', (e) => this.onClick(e));
      if (this.main && this.slides.length > 1) this.bindSwipe();
      if (this.main && this.dataset.zoom === 'true') this.bindZoom();
      if (this.dataset.hideControls === 'true') this.bindVideoToggle();
      this.updateNav();
      const c = this.closest('elexa-product');
      if (c && c.state) this.showVariant(c.state.variant, 'init');
      if (this.slides[this.index]) this.enter(this.slides[this.index], true);
    }

    onClick(e) {
      const thumb = e.target.closest('[data-ex-thumb]');
      if (thumb && this.contains(thumb)) return this.go(this.thumbs.indexOf(thumb));
      const dot = e.target.closest('[data-ex-dot]');
      if (dot && this.contains(dot)) return this.go(this.dots.indexOf(dot));
      if (e.target.closest('[data-ex-gallery-prev]')) return this.step(-1);
      if (e.target.closest('[data-ex-gallery-next]')) return this.step(1);
      return null;
    }

    visible() {
      return this.slides.map((s, i) => i).filter((i) => this.slides[i].dataset.exHidden !== 'true');
    }

    /** Dawn-compatible API (used by A2 per-unit rows): setActiveMedia('<section id>-<media id>'). */
    setActiveMedia(key) {
      const id = String(key == null ? '' : key).split('-').pop();
      const i = this.slides.findIndex((s) => s.dataset.mediaId === id);
      if (i >= 0) this.go(i);
    }

    step(dir) {
      const vis = this.visible();
      if (vis.length < 2) return;
      let pos = vis.indexOf(this.index);
      if (pos < 0) pos = 0;
      this.go(vis[(pos + dir + vis.length) % vis.length]);
    }

    go(i, { instant = false } = {}) {
      if (i == null || i < 0 || i >= this.slides.length) return;
      const prev = this.slides[this.index];
      const next = this.slides[i];
      if (instant || reduced()) {
        this.classList.add('is-instant');
        requestAnimationFrame(() => requestAnimationFrame(() => this.classList.remove('is-instant')));
      }
      if (prev && prev !== next) {
        prev.classList.remove('is-active', 'is-zooming');
        prev.setAttribute('aria-hidden', 'true');
        prev.inert = true;
        this.leave(prev);
      }
      next.classList.add('is-active');
      next.removeAttribute('aria-hidden');
      next.inert = false;
      this.index = i;
      [this.thumbs, this.dots].forEach((list) =>
        list.forEach((el, k) => {
          el.classList.toggle('is-active', k === i);
          if (k === i) el.setAttribute('aria-current', 'true');
          else el.removeAttribute('aria-current');
        })
      );
      if (next.dataset.ratio) this.style.setProperty('--ex-active-ratio', next.dataset.ratio);
      this.centerThumb(instant);
      if (prev !== next) this.enter(next, false);
    }

    centerThumb(instant) {
      const t = this.thumbs[this.index];
      const strip = this.strip;
      if (!t || !strip || t.hidden) return;
      const behavior = instant || reduced() ? 'auto' : 'smooth';
      const vertical = getComputedStyle(strip).flexDirection === 'column';
      if (vertical) {
        if (strip.scrollHeight <= strip.clientHeight) return;
        strip.scrollTo({ top: t.offsetTop - (strip.clientHeight - t.offsetHeight) / 2, behavior });
      } else {
        if (strip.scrollWidth <= strip.clientWidth) return;
        strip.scrollTo({ left: t.offsetLeft - (strip.clientWidth - t.offsetWidth) / 2, behavior });
      }
    }

    enter(slide, initial) {
      const iframe = slide.querySelector('iframe[data-src]');
      if (iframe && !iframe.getAttribute('src')) {
        let src = iframe.dataset.src;
        if (this.autoplay && !initial) src += `${src.includes('?') ? '&' : '?'}autoplay=1&mute=1&muted=1`;
        iframe.setAttribute('src', src);
      } else if (iframe && this.autoplay && !initial) {
        this.postToEmbed(iframe, 'play');
      }
      const video = slide.querySelector('video');
      if (video && this.autoplay && !(initial && slide !== this.slides[0])) {
        video.muted = true;
        video.playsInline = true;
        video.loop = this.loop;
        const p = video.play();
        if (p && p.catch) p.catch(() => {});
      }
    }

    leave(slide) {
      slide.querySelectorAll('video').forEach((v) => {
        v.pause();
        try {
          v.currentTime = 0;
        } catch (e) {
          /* not seekable yet */
        }
      });
      slide.querySelectorAll('iframe').forEach((f) => this.postToEmbed(f, 'pause'));
    }

    postToEmbed(frame, action) {
      const src = frame.getAttribute('src') || '';
      if (!src || !frame.contentWindow) return;
      if (/youtube/.test(src)) {
        frame.contentWindow.postMessage(
          JSON.stringify({ event: 'command', func: action === 'play' ? 'playVideo' : 'pauseVideo', args: '' }),
          '*'
        );
      } else if (/vimeo/.test(src)) {
        frame.contentWindow.postMessage(JSON.stringify({ method: action }), '*');
      }
    }

    bindVideoToggle() {
      this.slides.forEach((slide) => {
        const v = slide.querySelector('video');
        if (!v) return;
        v.addEventListener('click', () => {
          if (v.paused) {
            const p = v.play();
            if (p && p.catch) p.catch(() => {});
          } else v.pause();
        });
      });
    }

    bindSwipe() {
      const main = this.main;
      let t = null;
      main.addEventListener(
        'touchstart',
        (e) => {
          if (e.touches.length > 1) {
            if (t) t.pinch = true;
            return;
          }
          const p = e.touches[0];
          t = { x: p.clientX, y: p.clientY, dx: 0, dy: 0, armed: false, pinch: false, lock: false };
          const ref = t;
          setTimeout(() => {
            ref.armed = true;
          }, 50);
        },
        { passive: true }
      );
      main.addEventListener(
        'touchmove',
        (e) => {
          if (!t) return;
          if (e.touches.length > 1) {
            t.pinch = true;
            return;
          }
          if (t.pinch) return;
          const p = e.touches[0];
          t.dx = p.clientX - t.x;
          t.dy = p.clientY - t.y;
          if (!t.lock && Math.abs(t.dx) > 15 && Math.abs(t.dx) > Math.abs(t.dy)) t.lock = true;
          if (t.lock && e.cancelable) e.preventDefault();
        },
        { passive: false }
      );
      const end = () => {
        if (!t) return;
        const g = t;
        t = null;
        if (g.pinch || !g.armed || Math.abs(g.dx) <= 30 || Math.abs(g.dx) < Math.abs(g.dy)) return;
        let dir = g.dx < 0 ? 1 : -1;
        if (isRTL(this)) dir = -dir;
        this.step(dir);
      };
      main.addEventListener('touchend', end);
      main.addEventListener('touchcancel', () => {
        t = null;
      });
    }

    bindZoom() {
      const mq = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 750px)');
      const layer = () => {
        const s = this.slides[this.index];
        return s ? [s, s.querySelector('[data-ex-zoom]')] : [null, null];
      };
      const move = (e) => {
        const [s, z] = layer();
        if (!s || !z) return;
        const r = s.getBoundingClientRect();
        const x = Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100));
        const y = Math.min(100, Math.max(0, ((e.clientY - r.top) / r.height) * 100));
        z.style.backgroundPosition = `${x}% ${y}%`;
      };
      // The photo is only swapped for the zoom layer once the large image has loaded (no blank frame).
      const start = (e) => {
        if (e.pointerType !== 'mouse' || !mq.matches) return;
        this.zoomHover = true;
        const [s, z] = layer();
        if (!s || !z) return;
        move(e);
        if (s.classList.contains('is-zooming')) return;
        if (z.dataset.loaded === 'true') {
          s.classList.add('is-zooming');
          return;
        }
        if (z.exLoading) return;
        z.exLoading = true;
        const img = new Image();
        img.onload = () => {
          z.dataset.loaded = 'true';
          z.style.backgroundImage = `url("${z.dataset.src}")`;
          const [s2, z2] = layer();
          if (this.zoomHover && z2 === z) s2.classList.add('is-zooming');
        };
        img.onerror = () => {
          z.exLoading = false;
        };
        img.src = z.dataset.src;
      };
      this.main.addEventListener('pointerenter', start);
      this.main.addEventListener('pointermove', start);
      this.main.addEventListener('pointerleave', () => {
        this.zoomHover = false;
        const [s, z] = layer();
        if (!s) return;
        s.classList.remove('is-zooming');
        if (z) z.style.backgroundPosition = '50% 50%';
      });
    }

    showVariant(variant, source) {
      if (this.hideOthers) {
        const keep = variant && variant.featured_media ? Number(variant.featured_media.id) : null;
        this.slides.forEach((s, i) => {
          const id = Number(s.dataset.mediaId);
          const hide = Boolean(variant) && this.variantMedia.has(id) && id !== keep;
          s.dataset.exHidden = hide ? 'true' : 'false';
          if (this.thumbs[i]) this.thumbs[i].hidden = hide;
          if (this.dots[i]) this.dots[i].hidden = hide;
        });
      }
      const instant = source === 'init';
      const target =
        variant && variant.featured_media
          ? this.slides.findIndex((s) => Number(s.dataset.mediaId) === Number(variant.featured_media.id))
          : -1;
      if (target >= 0 && target !== this.index) this.go(target, { instant });
      else if (this.slides[this.index] && this.slides[this.index].dataset.exHidden === 'true') {
        const vis = this.visible();
        if (vis.length) this.go(vis[0], { instant });
      }
      this.updateNav();
    }

    updateNav() {
      const vis = this.visible();
      this.classList.toggle('is-single', vis.length < 2);
      const tpl = this.dataset.dotLabel || 'View image [n]';
      let n = 0;
      this.dots.forEach((d) => {
        if (d.hidden) return;
        n += 1;
        d.setAttribute('aria-label', fill(tpl, { n }));
      });
    }
  }

  /* ====================================================================== *
   * Product section controller
   * ====================================================================== */
  class ElexaProduct extends HTMLElement {
    connectedCallback() {
      if (this.state) return;
      const json = this.querySelector('script[data-ex-product-json]');
      if (!json) return;
      try {
        this.config = JSON.parse(json.textContent);
      } catch (e) {
        console.error('[Elexa product] invalid product JSON', e);
        return;
      }
      const c = this.config;
      this.sectionId = c.sectionId || this.dataset.sectionId;
      this.product = c.product || {};
      this.strings = c.strings || {};
      const extras = c.extras || {};
      this.variants = (this.product.variants || []).map((v) => {
        const x = extras[v.id];
        if (x) {
          v.inventory_quantity = x.q;
          v.inventory_policy = x.p;
          if (!v.inventory_management) v.inventory_management = x.m || null;
          if (!v.quantity_rule && x.r) v.quantity_rule = x.r;
          if (!v.featured_media && x.fm) v.featured_media = { id: x.fm };
        }
        v.quantity_rule = Object.assign({ min: 1, max: null, increment: 1 }, v.quantity_rule || {});
        if (!Array.isArray(v.options)) v.options = [v.option1, v.option2, v.option3].filter((o) => o != null);
        return v;
      });
      this.optionCount = Math.max(1, (this.product.options || []).length);
      this.form = this.querySelector('form[data-ex-product-form]');
      const initial = this.findVariant(c.initialVariantId);
      this.fallbackVariant = initial || this.findVariant(c.displayVariantId) || this.variants[0] || null;
      const options = initial ? initial.options.slice() : new Array(this.optionCount).fill(null);
      (c.hiddenOptions || []).forEach((pos) => {
        if (options[pos - 1] == null && this.fallbackVariant) options[pos - 1] = this.fallbackVariant.options[pos - 1];
      });
      this.state = {
        variant: initial,
        options,
        quantity: 1,
        sellingPlan: '',
        offer: null,
        subscription: null,
        unavailable: false,
      };
      this.state.quantity = this.alignQuantity(c.initialQuantity || 1);
      registry[this.sectionId] = this;
      this.listen();
      const early = Elexa.offers && typeof Elexa.offers.get === 'function' ? Elexa.offers.get(this.sectionId) : null;
      if (early) this.onOffer(early, true);
      this.writeForm();
      this.render();
      this.syncChildren('init', true);
      this.initSticky();
      if (c.seal && c.seal.enabled) this.sealPreselect(c.seal.planId);
      this.dataset.ready = 'true';
      emit(EV.ready, { sectionId: this.sectionId, variant: this.state.variant, product: this.product }, this);
    }

    disconnectedCallback() {
      (this.cleanups || []).forEach((fn) => fn());
      this.cleanups = [];
      clearInterval(this.sealTimer);
      if (registry[this.sectionId] === this) delete registry[this.sectionId];
      this.state = null;
    }

    /* ---------------- data helpers ---------------- */
    findVariant(id) {
      if (id == null || id === '') return null;
      return this.variants.find((v) => String(v.id) === String(id)) || null;
    }

    getState() {
      const s = this.state;
      return {
        variant: s.variant,
        options: s.options.slice(),
        quantity: s.quantity,
        sellingPlan: s.sellingPlan,
        offer: s.offer,
        subscription: s.subscription,
        unavailable: s.unavailable,
        buttonState: this.buttonState(),
        totals: this.totals(true),
      };
    }

    alignQuantity(q) {
      const v = (this.state && this.state.variant) || this.fallbackVariant || {};
      const r = v.quantity_rule || {};
      const min = Math.max(1, parseInt(r.min, 10) || 1);
      const step = Math.max(1, parseInt(r.increment, 10) || 1);
      const max = parseInt(r.max, 10) || null;
      let n = parseInt(q, 10);
      if (!Number.isFinite(n) || n < min) n = min;
      if (n % step) n = Math.max(min, Math.round(n / step) * step);
      if (max && n > max) n = Math.max(min, Math.floor(max / step) * step);
      return n;
    }

    /* ---------------- listeners ---------------- */
    listen() {
      this.cleanups = [];
      const on = (target, name, fn, opts) => {
        target.addEventListener(name, fn, opts);
        this.cleanups.push(() => target.removeEventListener(name, fn, opts));
      };
      on(document, EV.offer, (e) => this.onOffer(e.detail || {}));
      on(document, EV.variant, (e) => {
        const d = e.detail || {};
        if (this.emitting || d.sectionId !== this.sectionId || !d.variant) return;
        const v = this.findVariant(d.variant.id);
        if (v && v !== this.state.variant) this.applyVariant(v, { source: d.source || 'external', emit: false });
      });
      on(document, EV.option, (e) => {
        const d = e.detail || {};
        if (this.emitting || d.sectionId !== this.sectionId || !d.position) return;
        this.setOption(d.position, d.value, { source: d.source || 'external', silent: true });
      });
      on(document, EV.subscription, (e) => {
        const d = e.detail || {};
        if (d.sectionId !== this.sectionId) return;
        const adj = d.adjustment || (d.type ? { type: d.type, value: d.value } : null);
        this.state.subscription =
          d.sellingPlanId && adj && adj.type ? { type: adj.type, value: Number(adj.value) || 0, sellingPlanId: d.sellingPlanId } : null;
        if ('sellingPlanId' in d) this.setSellingPlan(d.sellingPlanId || '');
        this.syncPickers();
        this.render();
      });
      on(document, 'sealsubs:selling_plan_changed', (e) => {
        const d = e.detail || {};
        if (d.element && this.contains(d.element)) this.setSellingPlan(d.selling_plan_id || '');
      });
      if (this.form) {
        on(this.form, 'submit', (e) => {
          e.preventDefault();
          this.submit(e.submitter && e.submitter.matches('[data-ex-atc]') ? e.submitter : null);
        });
      }
      on(this, 'click', (e) => {
        const btn = e.target.closest('[data-ex-atc]');
        if (!btn || !this.contains(btn)) return;
        e.preventDefault();
        this.submit(btn);
      });
    }

    onOffer(d, silent) {
      if (String(d.sectionId) !== String(this.sectionId)) return;
      const items = Array.isArray(d.items) ? d.items.filter((i) => i && (i.id || i.variant_id)) : null;
      const total = d.totalCents === null || d.totalCents === undefined || d.totalCents === '' ? NaN : Number(d.totalCents);
      if (!items && !Number.isFinite(total)) {
        this.state.offer = null;
      } else {
        this.state.offer = {
          items: items || [],
          label: d.label || '',
          totalCents: Number.isFinite(total) ? Math.round(total) : null,
          compareCents: Number(d.compareCents) || 0,
          valid: d.valid !== false,
          error: d.error || '',
        };
      }
      if (!this.state.offer || this.state.offer.valid) this.clearError();
      if (silent && !this.dataset.ready) return;
      this.render();
      this.syncQty();
    }

    /* ---------------- state changes ---------------- */
    setOption(position, value, { source = 'picker', silent = false } = {}) {
      const i = parseInt(position, 10) - 1;
      if (!(i >= 0 && i < this.optionCount)) return;
      const before = this.state.options.slice();
      this.state.options[i] = value == null || value === '' ? null : String(value);
      if (!silent) {
        this.emitting = true;
        try {
          emit(EV.option, { sectionId: this.sectionId, position: i + 1, value: this.state.options[i], source });
        } finally {
          this.emitting = false;
        }
      }
      const opts = this.state.options;
      if (opts.some((o) => o == null)) {
        this.state.variant = null;
        this.state.unavailable = false;
        this.writeForm();
        this.render();
        this.syncChildren(source, false);
        return;
      }
      let match = this.variants.find((v) => v.options.every((o, k) => o === opts[k]));
      if (!match && before.every((o) => o != null)) {
        match = this.closestVariant(i, opts);
      }
      if (match) {
        this.applyVariant(match, { source });
      } else {
        this.state.variant = null;
        this.state.unavailable = true;
        this.writeForm();
        this.render();
        this.syncChildren(source, false);
      }
    }

    /** The variant that keeps the chosen value at position i (and the ones before it) with the most other choices. */
    closestVariant(i, opts) {
      let best = null;
      let bestScore = -1;
      this.variants.forEach((v) => {
        if (v.options[i] !== opts[i]) return;
        for (let k = 0; k < i; k++) if (v.options[k] !== opts[k]) return;
        let score = v.available ? 100 : 0;
        for (let k = i + 1; k < opts.length; k++) if (v.options[k] === opts[k]) score += 1;
        if (score > bestScore) {
          best = v;
          bestScore = score;
        }
      });
      return best;
    }

    selectVariant(idOrVariant, opts = {}) {
      const id = idOrVariant && typeof idOrVariant === 'object' ? idOrVariant.id : idOrVariant;
      const v = this.findVariant(id);
      if (v) this.applyVariant(v, opts);
      return v;
    }

    applyVariant(variant, { source = 'api', emit: fire = true } = {}) {
      this.state.variant = variant;
      this.state.unavailable = false;
      this.state.options = variant.options.slice();
      this.state.quantity = this.alignQuantity(this.state.quantity);
      this.clearError();
      this.hideSelectError();
      this.writeForm();
      this.updateUrl();
      this.render();
      this.syncChildren(source, true);
      if (fire) this.emitVariant(source);
    }

    emitVariant(source) {
      this.emitting = true;
      try {
        emit(EV.variant, {
          sectionId: this.sectionId,
          variant: this.state.variant,
          product: this.product,
          source,
          quantity: this.state.quantity,
        });
      } finally {
        this.emitting = false;
      }
      dawnPublish('variantChange', { data: { sectionId: this.sectionId, html: null, variant: this.state.variant } });
    }

    setQuantity(n, { source = 'quantity' } = {}) {
      const q = this.alignQuantity(n);
      const changed = q !== this.state.quantity;
      this.state.quantity = q;
      this.writeForm();
      this.render();
      this.syncQty();
      if (changed) {
        emit(EV.quantity, { sectionId: this.sectionId, quantity: q, source });
        dawnPublish('quantityUpdate', { sectionId: this.sectionId, quantity: q });
      }
      return q;
    }

    setSellingPlan(id) {
      this.state.sellingPlan = id ? String(id) : '';
      const field = this.form && this.form.querySelector('input[data-ex-selling-plan-field]');
      if (field) field.value = this.state.sellingPlan;
    }

    writeForm() {
      if (!this.form) return;
      const id = this.form.querySelector('input[name="id"]');
      if (id) {
        const next = this.state.variant ? String(this.state.variant.id) : '';
        id.disabled = false;
        if (id.value !== next) {
          id.value = next;
          id.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      const q = this.form.querySelector('input[data-ex-qty-field]');
      if (q && q.value !== String(this.state.quantity)) {
        q.value = this.state.quantity;
        q.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    updateUrl() {
      if (!this.config.updateUrl || !this.state.variant) return;
      if (this.closest('quick-add-modal')) return;
      const host = this.closest('product-info');
      if (host && host.dataset.updateUrl === 'false') return;
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', this.state.variant.id);
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (e) {
        /* ignore */
      }
    }

    /* ---------------- rendering ---------------- */
    buttonState() {
      const s = this.state;
      if (s.unavailable) return 'unavailable';
      if (s.variant) return s.variant.available ? 'ready' : 'soldout';
      if (this.variants.length && !this.variants.some((v) => v.available)) return 'soldout';
      return 'choose';
    }

    totals(lineTotal = true) {
      const s = this.state;
      if (s.offer && s.offer.totalCents != null) {
        return { price: s.offer.totalCents, compare: s.offer.compareCents || 0, offer: true };
      }
      const v = s.variant || this.fallbackVariant;
      if (!v) return { price: 0, compare: 0, offer: false };
      const m = lineTotal ? s.quantity : 1;
      return { price: v.price * m, compare: (v.compare_at_price || 0) * m, offer: false };
    }

    render() {
      if (!this.state) return;
      this.querySelectorAll('[data-ex-price-block]').forEach((el) => this.renderPrice(el));
      this.querySelectorAll('[data-ex-atc]').forEach((btn) => this.renderButton(btn));
      this.querySelectorAll('[data-ex-title]').forEach((el) => this.renderTitle(el));
      emit(EV.render, { sectionId: this.sectionId, state: this.getState() }, this);
    }

    renderPrice(el) {
      const t = this.totals(el.dataset.lineTotal === 'true');
      const discounted = t.compare > t.price;
      el.classList.toggle('is-discounted', discounted);
      const cur = el.querySelector('[data-ex-price-current]');
      if (cur) cur.innerHTML = money(t.price);
      const cmpWrap = el.querySelector('[data-ex-price-compare-wrap]');
      if (cmpWrap) cmpWrap.hidden = !discounted;
      const cmp = el.querySelector('[data-ex-price-compare]');
      if (cmp) cmp.innerHTML = discounted ? money(t.compare) : '';
      const badge = el.querySelector('[data-ex-price-badge]');
      if (!badge) return;
      const p = savePct(t.compare, t.price);
      const prefix = esc(el.dataset.badgePrefix || '');
      let label;
      if (el.dataset.badgeType === 'money_amount') label = `${prefix} ${money(t.compare - t.price)}`;
      else if (el.dataset.badgeType === 'custom' && el.dataset.badgeCustom) label = esc(el.dataset.badgeCustom);
      else label = `${prefix} ${p}%`;
      const text = badge.querySelector('[data-ex-badge-text]');
      if (text) text.innerHTML = label.trim();
      badge.hidden = !discounted || (el.dataset.badgeType !== 'custom' && el.dataset.badgeType !== 'money_amount' && p <= 0);
    }

    renderButton(btn) {
      const st = this.buttonState();
      const label = btn.querySelector('[data-ex-atc-label]');
      const off = st === 'soldout' || st === 'unavailable';
      let text = btn.dataset.label || this.strings.addToCart;
      if (st === 'soldout') text = this.strings.soldOut;
      else if (st === 'unavailable') text = this.strings.unavailable;
      if (label) label.textContent = text;
      if (!this.busy) btn.disabled = off;
      btn.classList.toggle('is-sold-out', off);
      const wrap = btn.querySelector('[data-ex-atc-price-wrap]');
      if (!wrap) return;
      const show = btn.dataset.showPrice === 'true' && st === 'ready';
      wrap.hidden = !show;
      if (!show) return;
      const t = this.totals(true);
      const price = wrap.querySelector('[data-ex-atc-price]');
      if (price) price.innerHTML = money(t.price);
      const cw = wrap.querySelector('[data-ex-atc-compare-wrap]');
      const cmp = wrap.querySelector('[data-ex-atc-compare]');
      if (cw) cw.hidden = !(t.compare > t.price);
      if (cmp) cmp.innerHTML = t.compare > t.price ? money(t.compare) : '';
    }

    renderTitle(el) {
      const n = parseInt(el.dataset.dynamicOption, 10);
      let text = el.dataset.fallback || '';
      const v = this.state.variant;
      if (n && v) {
        const val = v.options[n - 1];
        if (val && val !== 'Default Title') text = val;
      }
      if (el.textContent !== text) el.textContent = text;
    }

    syncPickers() {
      this.querySelectorAll('elexa-variant-picker').forEach((p) => typeof p.sync === 'function' && p.sync(this));
    }

    syncChildren(source, withGallery) {
      this.syncPickers();
      this.querySelectorAll('elexa-variant-bundle').forEach((b) => typeof b.sync === 'function' && b.sync(this, source));
      if (withGallery) {
        this.querySelectorAll('elexa-gallery').forEach(
          (g) => typeof g.showVariant === 'function' && g.showVariant(this.state.variant, source)
        );
      }
      this.syncQty();
    }

    syncQty() {
      const v = this.state.variant || this.fallbackVariant || {};
      const r = v.quantity_rule || {};
      const data = {
        value: this.state.quantity,
        min: Math.max(1, parseInt(r.min, 10) || 1),
        max: parseInt(r.max, 10) || null,
        step: Math.max(1, parseInt(r.increment, 10) || 1),
        locked: Boolean(this.state.offer && this.state.offer.totalCents != null),
      };
      this.querySelectorAll('elexa-qty').forEach((q) => typeof q.update === 'function' && q.update(data));
    }

    /* ---------------- messages ---------------- */
    showError(message) {
      this.querySelectorAll('[data-ex-atc-error]').forEach((box) => {
        const text = box.querySelector('[data-ex-atc-error-text]');
        if (text) text.textContent = message;
        box.hidden = false;
      });
      emit(EV.error, { sectionId: this.sectionId, message }, this);
    }

    clearError() {
      this.querySelectorAll('[data-ex-atc-error]').forEach((box) => {
        box.hidden = true;
      });
    }

    showSelectError() {
      const groups = Array.from(this.querySelectorAll('[data-ex-option-group]'));
      const group = groups.find((g) => this.state.options[parseInt(g.dataset.position, 10) - 1] == null) || groups[0];
      const line = group && group.querySelector('[data-ex-picker-error]');
      if (!line) {
        this.showError(this.strings.selectVariant);
        return;
      }
      this.hideSelectError();
      line.hidden = false;
      line.textContent = this.strings.selectVariant;
      line.scrollIntoView({ block: 'center', behavior: reduced() ? 'auto' : 'smooth' });
    }

    hideSelectError() {
      this.querySelectorAll('[data-ex-picker-error]').forEach((l) => {
        l.hidden = true;
      });
    }

    /* ---------------- add to cart ---------------- */
    visibleSellingPlan() {
      const marked = this.querySelector(
        '[data-ex-selling-plan]:is(:checked, [aria-checked="true"], [aria-pressed="true"], .is-selected, [data-selected="true"])'
      );
      if (marked) return marked.dataset.exSellingPlan || '';
      const fields = Array.from(this.querySelectorAll('[name="selling_plan"]')).filter((f) => !(this.form && f.form === this.form));
      for (const f of fields) {
        if (f.type === 'radio' || f.type === 'checkbox') {
          if (f.checked) return f.value || '';
        } else if (f.value) return f.value;
      }
      return '';
    }

    planFor(id, plan) {
      if (!plan) return '';
      const v = this.findVariant(id);
      if (!v) return '';
      const allocs = v.selling_plan_allocations;
      if (!Array.isArray(allocs) || !allocs.length) return '';
      return allocs.some((a) => String(a.selling_plan_id) === String(plan)) ? plan : '';
    }

    buildItems() {
      const s = this.state;
      const fd = this.form ? new FormData(this.form) : new FormData();
      const props = {};
      const extra = {};
      let plan = '';
      let hasFile = false;
      fd.forEach((value, key) => {
        if (typeof value !== 'string') {
          if (value && value.size) hasFile = true;
          return;
        }
        let m = key.match(/^properties\[(.+)\]$/);
        if (m) {
          if (value !== '') props[m[1]] = value;
          return;
        }
        m = key.match(/^items\[(\d+)\]\[(id|quantity|selling_plan)\]$/);
        if (m) {
          (extra[m[1]] = extra[m[1]] || { properties: {} })[m[2]] = value;
          return;
        }
        m = key.match(/^items\[(\d+)\]\[properties\]\[(.+)\]$/);
        if (m) {
          (extra[m[1]] = extra[m[1]] || { properties: {} }).properties[m[2]] = value;
          return;
        }
        if (key === 'selling_plan' && value) plan = value;
      });
      if (hasFile) return null;
      plan = plan || s.sellingPlan || this.visibleSellingPlan();
      const extras = Object.keys(extra)
        .sort((a, b) => a - b)
        .map((k) => extra[k])
        .filter((i) => i.id)
        .map((i) => {
          const item = { id: i.id, quantity: parseInt(i.quantity, 10) || 1 };
          if (i.selling_plan) item.selling_plan = i.selling_plan;
          if (Object.keys(i.properties).length) item.properties = i.properties;
          return item;
        });
      const main = [{ id: s.variant.id, quantity: s.quantity }].map((i) => {
        const id = i.id || i.variant_id;
        const item = { id, quantity: parseInt(i.quantity, 10) || 1 };
        const p = Object.assign({}, props, i.properties || {});
        if (Object.keys(p).length) item.properties = p;
        const sp = i.selling_plan || this.planFor(id, plan);
        if (sp) item.selling_plan = sp;
        return item;
      });
      return extras.concat(main);
    }

    setBusy(on, trigger) {
      this.busy = on;
      const els = new Set(this.querySelectorAll('[data-ex-atc]'));
      if (trigger) els.add(trigger);
      els.forEach((b) => {
        b.classList.toggle('loading', on);
        if (on) b.setAttribute('aria-disabled', 'true');
        else b.removeAttribute('aria-disabled');
        b.setAttribute('aria-busy', on ? 'true' : 'false');
        const sp = b.querySelector('.loading__spinner');
        if (sp) sp.classList.toggle('hidden', !on);
      });
      if (!on) this.render();
    }

    /** The live offer for this section: A2's registry first, then the last elexa:offer-change. */
    currentOffer() {
      const live = Elexa.offers && typeof Elexa.offers.get === 'function' ? Elexa.offers.get(this.sectionId) : null;
      if (live) return { items: Array.isArray(live.items) ? live.items : null, valid: live.valid !== false, error: live.error || '' };
      const o = this.state.offer;
      return o ? { items: o.items.length ? o.items : null, valid: o.valid !== false, error: o.error } : null;
    }

    async submit(trigger) {
      if (this.busy || !this.state) return false;
      const st = this.buttonState();
      if (st === 'choose') {
        this.showSelectError();
        return false;
      }
      if (st !== 'ready') return false;
      const offer = this.currentOffer();
      if (offer && offer.valid === false) {
        this.showError(offer.error || this.strings.error);
        return false;
      }
      let items;
      if (offer && offer.items && offer.items.length) {
        // Offer blocks own the whole request: post exactly their list (gift, main and add-on lines).
        items = offer.items.map((i) => {
          const item = { id: i.id || i.variant_id, quantity: parseInt(i.quantity, 10) || 1 };
          if (i.properties && Object.keys(i.properties).length) item.properties = i.properties;
          if (i.selling_plan) item.selling_plan = i.selling_plan;
          return item;
        });
      } else {
        items = this.buildItems();
      }
      const cart = Elexa.cart;
      if (!items || !cart || typeof cart.add !== 'function') {
        if (this.form) this.form.submit();
        return false;
      }
      const atc = this.querySelector('[data-ex-atc]');
      const skip = Boolean(atc && atc.dataset.behavior === 'skip_to_checkout');
      const modal = this.closest('quick-add-modal');
      const options = { open: !skip && !modal, source: 'product-form' };
      this.clearError();
      this.setBusy(true, trigger);
      let navigating = false;
      try {
        try {
          await cart.add(items, options);
        } catch (first) {
          // A sold-out gift must never block the purchase: retry once without gift lines.
          const withoutGifts = items.filter((i) => !(i.properties && i.properties._elexa_gift));
          if (withoutGifts.length === items.length || !withoutGifts.length) throw first;
          await cart.add(withoutGifts, options);
        }
        if (skip) {
          navigating = true;
          window.location.href = this.config.checkoutUrl || '/checkout';
          return true;
        }
        if (modal) {
          document.body.addEventListener('modalClosed', () => setTimeout(() => cart.open()), { once: true });
          if (typeof modal.hide === 'function') modal.hide(true);
        }
        return true;
      } catch (err) {
        const message = (err && err.message) || this.strings.error || 'Error';
        this.showError(message);
        if (/sold out|out of stock/i.test(message) && this.state.variant && items.length === 1) {
          this.state.variant.available = false;
        }
        return false;
      } finally {
        if (!navigating) this.setBusy(false, trigger);
      }
    }

    /* ---------------- sticky gallery offset ---------------- */
    initSticky() {
      if (!this.querySelector('[data-ex-sticky-media]')) return;
      let frame = null;
      const update = () => {
        frame = null;
        let offset = 0;
        const header = document.querySelector('.shopify-section-header-sticky, [data-ex-sticky-header]');
        if (header) {
          const pos = getComputedStyle(header).position;
          if (pos === 'sticky' || pos === 'fixed') offset = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
        }
        this.style.setProperty('--ex-sticky-top', `${offset + 20}px`);
      };
      const queue = () => {
        if (!frame) frame = requestAnimationFrame(update);
      };
      update();
      window.addEventListener('scroll', queue, { passive: true });
      window.addEventListener('resize', queue, { passive: true });
      this.cleanups.push(() => {
        window.removeEventListener('scroll', queue);
        window.removeEventListener('resize', queue);
      });
    }

    /* ---------------- SEAL Subscriptions preselect ---------------- */
    sealPreselect(planId) {
      const id = String(planId == null ? '' : planId).trim();
      if (id && !/^\d+$/.test(id)) return;
      let elapsed = 0;
      this.sealTimer = setInterval(() => {
        elapsed += 400;
        let widgets = this.querySelectorAll('.sealsubs-target-element');
        if (!widgets.length) widgets = document.querySelectorAll('.sealsubs-target-element');
        if (!widgets.length && elapsed < 5000) return;
        clearInterval(this.sealTimer);
        widgets.forEach((w) =>
          w.dispatchEvent(new CustomEvent('sealsubs:change_selected_selling_plan_id', { detail: { selling_plan_id: id } }))
        );
      }, 400);
    }
  }

  // Children first, so they are upgraded before the controller syncs them.
  if (!customElements.get('elexa-variant-picker')) customElements.define('elexa-variant-picker', ElexaVariantPicker);
  if (!customElements.get('elexa-variant-bundle')) customElements.define('elexa-variant-bundle', ElexaVariantBundle);
  if (!customElements.get('elexa-qty')) customElements.define('elexa-qty', ElexaQty);
  if (!customElements.get('elexa-gallery')) customElements.define('elexa-gallery', ElexaGallery);
  customElements.define('elexa-product', ElexaProduct);

})();
